import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";

export type MenuItemRow = {
  id: string;
  slug: string;
  name: string;
  category: string;
  price: string;
  tagline: string;
  description: string;
  ingredients: string[];
  pairings: string[];
  image_url: string | null;
  badge: string | null;
  published: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

const COLS =
  "id,slug,name,category,price,tagline,description,ingredients,pairings,image_url,badge,published,sort_order,created_at,updated_at";

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

/** Public: list every menu row (published and hidden). Merge layer decides visibility. */
export const listPublishedMenu = createServerFn({ method: "GET" }).handler(
  async (): Promise<MenuItemRow[]> => {
    const supabase = publicClient();
    const { data, error } = await supabase
      .from("menu_items")
      .select(COLS)
      .order("sort_order", { ascending: true })
      .order("name", { ascending: true });
    if (error) return [];
    return (data ?? []) as MenuItemRow[];
  },
);

/** Public: fetch a single published item by slug. */
export const getPublishedMenuItem = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) =>
    z.object({ slug: z.string().min(1).max(200) }).parse(data),
  )
  .handler(async ({ data }): Promise<MenuItemRow | null> => {
    const supabase = publicClient();
    const { data: row, error } = await supabase
      .from("menu_items")
      .select(COLS)
      .eq("published", true)
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) return null;
    return (row ?? null) as MenuItemRow | null;
  });


/** Staff: list every menu item. */
export const listAllMenu = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<MenuItemRow[]> => {
    const { data, error } = await context.supabase
      .from("menu_items")
      .select(COLS)
      .order("category", { ascending: true })
      .order("sort_order", { ascending: true })
      .order("name", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as MenuItemRow[];
  });

const UpsertSchema = z.object({
  id: z.string().uuid().optional(),
  slug: z
    .string()
    .trim()
    .min(2)
    .max(120)
    .regex(/^[a-z0-9-]+$/, "Slug must be lowercase letters, numbers and dashes only."),
  name: z.string().trim().min(2).max(160),
  category: z.string().trim().min(2).max(80),
  price: z.string().trim().min(1).max(20),
  tagline: z.string().trim().max(240).optional().default(""),
  description: z.string().trim().max(2000).optional().default(""),
  ingredients: z.array(z.string().trim().min(1).max(80)).max(30).optional().default([]),
  pairings: z.array(z.string().trim().min(1).max(80)).max(20).optional().default([]),
  image_url: z
    .string()
    .trim()
    .max(2000)
    .optional()
    .nullable()
    .refine(
      (v) => !v || /^https?:\/\//i.test(v) || v.startsWith("/api/public/media/"),
      "Image must be a URL or an uploaded media path.",
    ),

  badge: z.string().trim().max(40).optional().nullable(),
  published: z.boolean().default(true),
  sort_order: z.number().int().min(0).max(9999).default(0),
});

export const upsertMenuItem = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => UpsertSchema.parse(data))
  .handler(async ({ data, context }) => {
    const payload = {
      slug: data.slug,
      name: data.name,
      category: data.category,
      price: data.price,
      tagline: data.tagline ?? "",
      description: data.description ?? "",
      ingredients: data.ingredients ?? [],
      pairings: data.pairings ?? [],
      image_url: data.image_url || null,
      badge: data.badge || null,
      published: data.published,
      sort_order: data.sort_order,
    };
    if (data.id) {
      const { error } = await context.supabase
        .from("menu_items")
        .update(payload as never)
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: row, error } = await context.supabase
      .from("menu_items")
      .insert(payload as never)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id as string };
  });

export const deleteMenuItem = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("menu_items").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
